import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { Shield, CheckCircle, AlertCircle, GripVertical } from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { getPublicSurvey, submitPublicSurvey } from '../../services/surveyService';
import theme from '../../assets/styles/theme';
import Button from '../../components/ui/Button';

const COOKIE_PREFIX = 'flexbee_ps_';

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

export default function PublicSurveyPage() {
  const { t } = useTranslation();
  const { token } = useParams();
  const [stage, setStage] = useState('loading'); // loading, consent, filling, submitted, error
  const [survey, setSurvey] = useState(null);
  const [errorKey, setErrorKey] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [fingerprint, setFingerprint] = useState(null);
  const [identity, setIdentity] = useState({ firstName: '', lastName: '', email: '', phone: '', organization: '' });

  // Load fingerprint
  useEffect(() => {
    FingerprintJS.load().then(fp => fp.get()).then(result => {
      setFingerprint(result.visitorId);
    });
  }, []);

  // Load survey
  useEffect(() => {
    // Cookie check
    getPublicSurvey(token)
      .then(res => {
        const data = res.data?.data;
        if (!data) {
          setErrorKey('survey.notFound');
          setStage('error');
          return;
        }
        if (data.isExpired) {
          setErrorKey('survey.surveyExpired');
          setStage('error');
          return;
        }
        if (data.isMaxResponsesReached) {
          setErrorKey('survey.surveyMaxReached');
          setStage('error');
          return;
        }
        setSurvey(data);
        const cookieKey = COOKIE_PREFIX + data.id;
        if (getCookie(cookieKey)) {
          setErrorKey('survey.alreadySubmitted');
          setStage('error');
        } else {
          setStage('consent');
        }
      })
      .catch(() => {
        setErrorKey('survey.notFound');
        setStage('error');
      });
  }, [token]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } })
  );
  const questions = useMemo(() => survey?.questions || [], [survey]);

  const setAnswer = useCallback((qId, value) => {
    setAnswers(prev => ({ ...prev, [qId]: value }));
  }, []);

  const toggleMulti = useCallback((qId, optId, maxSel) => {
    setAnswers(prev => {
      const current = prev[qId] || [];
      if (current.includes(optId)) {
        return { ...prev, [qId]: current.filter(v => v !== optId) };
      }
      const limit = parseInt(maxSel, 10);
      if (!isNaN(limit) && limit > 0 && current.length >= limit) {
        return prev;
      }
      return { ...prev, [qId]: [...current, optId] };
    });
  }, []);

  const answeredCount = questions.filter(q => {
    const a = answers[q.id];
    if (q.questionType === 'freeText') return a && a.trim();
    if (q.questionType === 'singleChoice' || q.questionType === 'singleChoiceAlternate') return a !== undefined && a !== null;
    if (q.questionType === 'ranking') return a && a.length > 0;
    return a && a.length > 0;
  }).length;

  const progressPct = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

  const handleSubmit = useCallback(async () => {
    const honeypot = document.getElementById('ps_website')?.value;
    const payload = questions.map(q => {
      const a = answers[q.id];
      if (q.questionType === 'singleChoice') return { questionId: q.id, selectedOptionId: a };
      if (q.questionType === 'singleChoiceAlternate') {
        return { questionId: q.id, selectedOptionId: a, alternateText: (answers[`${q.id}_other`] || '').trim() || undefined };
      }
      if (q.questionType === 'multipleChoice') return { questionId: q.id, selectedOptionIds: a || [] };
      if (q.questionType === 'multipleChoiceAlternate') {
        return {
          questionId: q.id,
          selectedOptionIds: (a || []).filter(v => typeof v !== 'string' || !v.startsWith('other:')),
          alternateText: (answers[`${q.id}_other`] || '').trim() || undefined,
        };
      }
      if (q.questionType === 'ranking') return { questionId: q.id, selectedOptionIds: (a || []).map(item => item.id) };
      if (q.questionType === 'freeText') return { questionId: q.id, textValue: a || '' };
      return { questionId: q.id };
    });

    setSubmitting(true);
    try {
      await submitPublicSurvey(token, {
        answers: payload,
        consentAccepted: true,
        browserFingerprint: fingerprint,
        honeypotField: honeypot || null,
        firstName: identity.firstName.trim() || null,
        lastName: identity.lastName.trim() || null,
        email: identity.email.trim() || null,
        phone: identity.phone.trim() || null,
        organization: identity.organization.trim() || null,
      });
      setCookie(COOKIE_PREFIX + survey.id, '1', 365);
      setStage('submitted');
    } catch {
      // interceptor handles error toast
    } finally {
      setSubmitting(false);
    }
  }, [token, survey, questions, answers, fingerprint, identity]);

  return (
    <ThemeProvider theme={theme}>
      <PageWrapper>
        <PageContent>
          {stage === 'loading' && <CenterText>...</CenterText>}

          {stage === 'error' && (
            <StatusCard>
              <AlertCircle size={48} color="#E53935" />
              <StatusTitle>{t(errorKey)}</StatusTitle>
            </StatusCard>
          )}

          {stage === 'consent' && (
            <ConsentCard>
              <SurveyTitle>{survey.title}</SurveyTitle>
              {survey.description && <SurveyDesc>{survey.description}</SurveyDesc>}

              <IdentityGrid>
                <IdentityField>
                  <IdentityLabel>{t('survey.firstName')} *</IdentityLabel>
                  <IdentityInput
                    value={identity.firstName}
                    onChange={(e) => setIdentity({ ...identity, firstName: e.target.value })}
                  />
                </IdentityField>
                <IdentityField>
                  <IdentityLabel>{t('survey.lastName')} *</IdentityLabel>
                  <IdentityInput
                    value={identity.lastName}
                    onChange={(e) => setIdentity({ ...identity, lastName: e.target.value })}
                  />
                </IdentityField>
                <IdentityField>
                  <IdentityLabel>{t('survey.email')}</IdentityLabel>
                  <IdentityInput
                    type="email"
                    value={identity.email}
                    onChange={(e) => setIdentity({ ...identity, email: e.target.value })}
                  />
                </IdentityField>
                <IdentityField>
                  <IdentityLabel>{t('survey.phone')}</IdentityLabel>
                  <IdentityInput
                    value={identity.phone}
                    onChange={(e) => setIdentity({ ...identity, phone: e.target.value })}
                  />
                </IdentityField>
                <IdentityFieldFull>
                  <IdentityLabel>{t('survey.organization')}</IdentityLabel>
                  <IdentityInput
                    value={identity.organization}
                    onChange={(e) => setIdentity({ ...identity, organization: e.target.value })}
                  />
                </IdentityFieldFull>
              </IdentityGrid>

              <ConsentSection>
                <ConsentTitle>
                  <Shield size={18} color="#0291EF" />
                  {t('survey.consentTitle')}
                </ConsentTitle>
                <ConsentText>{t('survey.consentText')}</ConsentText>
                <ConsentCheckRow onClick={() => setConsentChecked(!consentChecked)}>
                  <ConsentCheckbox $checked={consentChecked}>
                    {consentChecked && <CheckMark />}
                  </ConsentCheckbox>
                  <span>{t('survey.consentCheckbox')}</span>
                </ConsentCheckRow>
              </ConsentSection>
              <Button
                fullWidth
                onClick={() => setStage('filling')}
                disabled={!consentChecked || !identity.firstName.trim() || !identity.lastName.trim()}
              >
                {t('survey.startSurvey')}
              </Button>
            </ConsentCard>
          )}

          {stage === 'filling' && (
            <>
              <SurveyTitle>{survey.title}</SurveyTitle>
              <ProgressText>{answeredCount}/{questions.length} ({progressPct}%)</ProgressText>
              <ProgressBar><ProgressFill $pct={progressPct} /></ProgressBar>

              {/* Honeypot */}
              <input type="text" id="ps_website" name="website" style={{ position: 'absolute', left: '-9999px', tabIndex: -1 }} autoComplete="off" />

              {questions.map((q, idx) => (
                <div key={q.id}>
                  {q.sectionTitle && (idx === 0 || questions[idx - 1]?.sectionTitle !== q.sectionTitle) && (
                    <SectionHeader>{q.sectionTitle}</SectionHeader>
                  )}
                <QuestionCard>
                  <QuestionText>
                    {idx + 1}. {q.questionText}
                    {q.isRequired && <Req>*</Req>}
                  </QuestionText>

                  {(q.questionType === 'singleChoice' || q.questionType === 'singleChoiceAlternate') && (
                    <>
                      {q.options.map(opt => (
                        <RadioOpt key={opt.id} $sel={answers[q.id] === opt.id} onClick={() => setAnswer(q.id, opt.id)}>
                          <RadioDot $sel={answers[q.id] === opt.id} />
                          <span>{opt.label}</span>
                          {opt.score != null && opt.score !== 0 && <Score>({opt.score})</Score>}
                        </RadioOpt>
                      ))}
                      {q.questionType === 'singleChoiceAlternate' && (
                        <OtherInput
                          placeholder={t('survey.other')}
                          value={answers[`${q.id}_other`] || ''}
                          onChange={e => setAnswers(prev => ({ ...prev, [`${q.id}_other`]: e.target.value }))}
                        />
                      )}
                    </>
                  )}

                  {(q.questionType === 'multipleChoice' || q.questionType === 'multipleChoiceAlternate') && (
                    <>
                      {q.maxSelections > 0 && (
                        <div style={{ fontSize: 12, color: '#A0A8B4', marginBottom: 8 }}>
                          {t('survey.maxSelectionsHint', { count: q.maxSelections })}
                          {' '}({(answers[q.id] || []).length}/{q.maxSelections})
                        </div>
                      )}
                      {q.options.map(opt => {
                        const sel = (answers[q.id] || []).includes(opt.id);
                        const limit = q.maxSelections ? Number(q.maxSelections) : 0;
                        const atLimit = limit > 0 && (answers[q.id] || []).length >= limit && !sel;
                        return (
                          <CheckOpt key={opt.id} $sel={sel} onClick={() => toggleMulti(q.id, opt.id, q.maxSelections)} style={atLimit ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}>
                            <CheckBox $sel={sel}>{sel && <CheckMark />}</CheckBox>
                            <span>{opt.label}</span>
                          </CheckOpt>
                        );
                      })}
                      {q.questionType === 'multipleChoiceAlternate' && (
                        <OtherInput
                          placeholder={t('survey.other')}
                          value={answers[`${q.id}_other`] || ''}
                          onChange={e => setAnswers(prev => ({ ...prev, [`${q.id}_other`]: e.target.value }))}
                        />
                      )}
                    </>
                  )}

                  {q.questionType === 'ranking' && (() => {
                    if (!answers[q.id]) {
                      const initial = q.options.map(o => ({ id: o.id, label: o.label }));
                      setTimeout(() => setAnswers(prev => ({ ...prev, [q.id]: initial })), 0);
                    }
                    const items = answers[q.id] || q.options.map(o => ({ id: o.id, label: o.label }));
                    return (
                      <>
                        <div style={{ fontSize: 12, color: '#A0A8B4', marginBottom: 8 }}>{t('survey.rankingHint')}</div>
                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => {
                          const { active, over } = event;
                          if (!over || active.id === over.id) return;
                          setAnswers(prev => {
                            const current = prev[q.id] || [];
                            const oldIdx = current.findIndex(i => i.id === active.id);
                            const newIdx = current.findIndex(i => i.id === over.id);
                            return { ...prev, [q.id]: arrayMove(current, oldIdx, newIdx) };
                          });
                        }}>
                          <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
                            {items.map((item, ri) => (
                              <SortablePublicRankItem key={item.id} item={item} index={ri} />
                            ))}
                          </SortableContext>
                        </DndContext>
                      </>
                    );
                  })()}

                  {q.questionType === 'freeText' && (
                    <TextArea
                      placeholder={t('survey.freeText')}
                      value={answers[q.id] || ''}
                      onChange={e => setAnswer(q.id, e.target.value)}
                    />
                  )}
                </QuestionCard>
                </div>
              ))}

              <Button fullWidth style={{ marginTop: 8, marginBottom: 40 }} onClick={handleSubmit} disabled={submitting}>
                {submitting ? '...' : t('survey.submit')}
              </Button>
            </>
          )}

          {stage === 'submitted' && (
            <StatusCard>
              <CheckCircle size={48} color="#02D3BF" />
              <StatusTitle>{t('survey.thankYou')}</StatusTitle>
              <StatusSub>{t('survey.thankYouMessage')}</StatusSub>
            </StatusCard>
          )}
        </PageContent>
      </PageWrapper>
    </ThemeProvider>
  );
}

const CheckMark = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const PublicRankItemStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 4px;
  margin-bottom: 6px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  color: #004242;
  background: #fef2f2;
  border: 1.5px solid #fecaca;
  cursor: grab;
`;

function SortablePublicRankItem({ item, index }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <PublicRankItemStyled ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <GripVertical size={14} style={{ color: '#A0A8B4', flexShrink: 0 }} />
      <span style={{ fontWeight: 700, color: '#dc2626', minWidth: 20 }}>{index + 1}.</span>
      <span>{item.label}</span>
    </PublicRankItemStyled>
  );
}

// --- Styled Components ---

const PageWrapper = styled.div`
  min-height: 100vh;
  background: #F5F7FA;
  display: flex;
  justify-content: center;
  padding: 24px 16px;
  font-family: 'Nunito Sans', sans-serif;

  @media (max-width: 600px) {
    padding: 12px 8px;
  }
`;

const PageContent = styled.div`
  width: 100%;
  max-width: 680px;
  position: relative;
`;

const CenterText = styled.div`
  text-align: center;
  padding: 80px 0;
  color: #A0A8B4;
  font-size: 16px;
`;

const SurveyTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #004242;
  margin: 0 0 8px;
`;

const SurveyDesc = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0 0 20px;
  line-height: 1.5;
`;

const ConsentCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 2px 12px rgba(0,66,66,0.08);
`;

const IdentityGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin: 20px 0 4px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const IdentityField = styled.div`
  display: flex; flex-direction: column; gap: 4px;
`;

const IdentityLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #004242;
`;

const IdentityInput = styled.input`
  height: 40px;
  padding: 0 12px;
  border: 1px solid #E0E4E8;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  color: #004242;
  background: #fff;
  &:focus { outline: none; border-color: #0291EF; }
`;

const IdentityFieldFull = styled(IdentityField)`
  grid-column: span 2;
  @media (max-width: 560px) { grid-column: span 1; }
`;

const ConsentSection = styled.div`
  background: #F5F7FA;
  border-radius: 8px;
  padding: 20px;
  margin: 20px 0 24px;
`;

const ConsentTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: #004242;
  margin-bottom: 12px;
`;

const ConsentText = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 16px;
  white-space: pre-line;
`;

const ConsentCheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #004242;
  font-weight: 600;
`;

const ConsentCheckbox = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 2px solid ${({ $checked }) => $checked ? '#0291EF' : '#D0D5DD'};
  background: ${({ $checked }) => $checked ? '#0291EF' : '#fff'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

// #72 — yerel StartButton kaldırıldı, paylaşılan `Button fullWidth`a
// bağlandı (bkz. t6-report.md).

const ProgressText = styled.div`
  font-size: 12px;
  color: #A0A8B4;
  text-align: right;
  margin-bottom: 6px;
`;

const ProgressBar = styled.div`
  background: #E8ECF0;
  border-radius: 4px;
  height: 8px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #0291EF;
  border-radius: 4px;
  transition: width 0.3s;
  width: ${({ $pct }) => $pct}%;
`;

const SectionHeader = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: #004242;
  padding: 16px 0 8px;
  border-bottom: 2px solid #0291EF;
  margin-bottom: 12px;
`;

const QuestionCard = styled.div`
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0,66,66,0.06);
`;

const QuestionText = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #004242;
  margin-bottom: 14px;
`;

const Req = styled.span`color: #ef4444; margin-left: 4px;`;

const RadioOpt = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 6px;
  font-size: 14px;
  color: #004242;
  background: ${({ $sel }) => $sel ? '#eff6ff' : '#f8fafc'};
  border: 1.5px solid ${({ $sel }) => $sel ? '#2563eb' : 'transparent'};
  transition: all 0.15s;
  &:hover { background: #eff6ff; }
`;

const RadioDot = styled.div`
  width: 18px; height: 18px; border-radius: 50%;
  border: 2px solid ${({ $sel }) => $sel ? '#2563eb' : '#D0D5DD'};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  &::after {
    content: ''; width: 10px; height: 10px; border-radius: 50%;
    background: ${({ $sel }) => $sel ? '#2563eb' : 'transparent'};
  }
`;

const CheckOpt = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 6px;
  font-size: 14px;
  color: #004242;
  background: ${({ $sel }) => $sel ? '#f0fdf4' : '#f8fafc'};
  border: 1.5px solid ${({ $sel }) => $sel ? '#16a34a' : 'transparent'};
  transition: all 0.15s;
  &:hover { background: #f0fdf4; }
`;

const CheckBox = styled.div`
  width: 18px; height: 18px; border-radius: 4px;
  border: 2px solid ${({ $sel }) => $sel ? '#16a34a' : '#D0D5DD'};
  background: ${({ $sel }) => $sel ? '#16a34a' : '#fff'};
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
`;

const Score = styled.span`font-size: 11px; color: #A0A8B4; margin-left: auto;`;

const OtherInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  margin-top: 8px;
  border: 1px solid #D0D5DD;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #0291EF; }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #D0D5DD;
  border-radius: 4px;
  font-family: 'Nunito Sans', sans-serif;
  font-size: 14px;
  min-height: 100px;
  resize: vertical;
  outline: none;
  box-sizing: border-box;
  &:focus { border-color: #0291EF; }
`;

// #72 — yerel SubmitButton kaldırıldı, paylaşılan `Button fullWidth`a
// bağlandı (bkz. t6-report.md).

const StatusCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 48px 32px;
  text-align: center;
  box-shadow: 0 2px 12px rgba(0,66,66,0.08);
  margin-top: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const StatusTitle = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: #004242;
`;

const StatusSub = styled.div`
  font-size: 14px;
  color: #64748b;
`;
